import uniqid from "uniqid";
import { useSelector } from "react-redux";
import { useState, forwardRef, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import useMobileDetect from "@smarthop/hooks/useMobileDetect";
import Alert from "@material-ui/lab/Alert";

import SmarthopDialogViewContainer from "@smarthop/views/SmarthopDialogViewContainer";
import SmarthopDialogViewLoadingStub from "@smarthop/views/SmarthopDialogViewLoadingStub";

import { openFormDialog } from "app/store/tools/formDialogSlice";
import { getTripFiles } from "app/services/tripsServices";
import { getUserTier } from "app/services/LoginService";
import { isEnabled } from "app/services/featureStorageService";

// Public Services
import { getTripFilesPublic } from "app/services/publicServices";
import FilesView from "app/main/files/FilesView";
import FilesUploadView from "app/main/files/FilesUploadView";

import { processTierRestrictions } from "app/main/utils/rolesUtils";
import { CircularProgress } from "@material-ui/core";

const TripFilesForm = forwardRef(({ nativeMobile, dataIds, setTitle, setSize }, ref) => {
	const dispatch = useDispatch();
	const isMobile = useMobileDetect();

	const filesRevision = useSelector(({ tools }) => tools.revision["filesRevision"]);

	const [data, setData] = useState([]);
	const [initialize, setInitialize] = useState(false);
	const [loading, setLoading] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [revision, setRevision] = useState(false);
	const [errorFiles, setErrorFiles] = useState(null);

	const userTier = getUserTier();
	const fileNameId = uniqid("file_id_");
	const fileInputRef = useRef();

	const userId = dataIds?.userId;
	const carrierId = dataIds?.carrierId;
	const tripId = dataIds?.tripId;
	const isPublicView = dataIds?.public ?? false;

	const isNative = window?.ReactNativeWebView ?? false;
	const processRestrictions = processTierRestrictions({ restrictions: ["TIER_PROFESSIONAL"], tier: userTier });
	const isEnabledFilesAnalysis = isPublicView
		? dataIds.showFilesAnalysis
		: isEnabled("ANALIZE_FILES_UPLOADED") && processRestrictions;

	useEffect(() => {
		if (!data?.data?.load_id) {
			setTitle?.("Edit Trip Files");
		} else {
			setTitle?.(`Edit Trip Files - Load ID ${data?.data?.load_id} `);
		}
		setSize?.("max-w-3xl");
		// eslint-disable-next-line
	}, [data]);

	useEffect(() => {
		if (!carrierId || !tripId) return;

		const processFlag = initialize || data.length > 0 ? false : true;
		setProcessing(processFlag);

		const serviceFunction = isPublicView ? getTripFilesPublic : getTripFiles;
		serviceFunction(carrierId, tripId)
			.then((res) => {
				setData(res?.pod_files);
				setProcessing(false);
				setInitialize(true);
			})
			.catch((e) => {
				setErrorFiles(e.response?.data?.errors?.[0]?.message ?? e.message ?? "Something went wrong...");
				setProcessing(false);
				setInitialize(true);
			});

		// eslint-disable-next-line
	}, [carrierId, tripId, isPublicView, filesRevision]);

	const startCall = async () => {
		let auxData = [];

		await getTripFilesPublic(carrierId, tripId)
			.then((res) => {
				auxData = res?.pod_files;
				setData(res?.pod_files);
			})
			.catch((e) => setErrorFiles(e.response?.data?.errors?.[0]?.message ?? e.message ?? "Something went wrong..."));

		const auxInProgress = auxData.filter((item) => item.analize?.status === "PROGRESS");
		if (auxInProgress.length === 0) return setRevision(false);

		setTimeout(startCall, 10000);
	};

	const openPaywallView = () => {
		dispatch(
			openFormDialog({
				viewId: "PAYWALL_VIEW",
				dataIds: {
					type: "automate_functionality_restriction",
					data: { dataIdsInherited: dataIds, callbackKey: "UPLOAD_FILES_FORM" },
				},
			})
		);
	};

	if (processing) {
		return <SmarthopDialogViewLoadingStub nativeMobile={nativeMobile} loading={processing} />;
	}

	return (
		<>
			<SmarthopDialogViewContainer
				nativeMobile={nativeMobile || isMobile}
				processing={loading}
				sideComponents={[
					{
						key: "FILES_PREVIEW",
						type: "PREVIEW",
						tabKeys: ["DOCUMENTS"],
						hideOnDesktop: errorFiles,
						hideOnMobile: false,
						ratio: "5/12",
						component: (
							<FilesUploadView
								fileInputRef={fileInputRef}
								userId={userId}
								tripId={tripId}
								carrierId={carrierId}
								data={data}
								isPublicView={isPublicView}
								native={isNative}
								setData={(data) => {
									setData(data);
									if (!revision) {
										setRevision(true);
										startCall();
									}
								}}
								setLoading={setLoading}
							/>
						),
					},
				]}
				tabComponents={[
					{
						key: "DOCUMENTS",
						title: "Documents",
						component: (
							<div className="flex flex-col justify-between w-full h-full md:overflow-hidden">
								{loading && (
									<div
										className={"absolute items-center justify-center top-1/2"}
										style={{ left: isMobile ? "45%" : "50%", top: isMobile ? "40%" : "" }}
									>
										<CircularProgress className="text-grey-400" />
									</div>
								)}

								<div className="flex flex-col w-full h-full overflow-hidden md:overflow-y-auto">
									<FilesView
										userId={userId}
										tripId={tripId}
										carrierId={carrierId}
										data={data}
										isPublicView={isPublicView}
										isEnabledFilesAnalysis={isEnabledFilesAnalysis}
										native={isNative}
										errorFiles={errorFiles}
										loading={loading}
										setLoading={setLoading}
									/>
								</div>
								{!nativeMobile &&
									!processTierRestrictions({ restrictions: ["TIER_PROFESSIONAL"], tier: userTier }) &&
									!isPublicView && (
										<div className="mb-5">
											<Alert severity="info">
												Want to process files automatically?
												<Link className="cursor-pointer px-4" onClick={() => openPaywallView()}>
													<span className="text-primary font-semibold underline">Click here</span>
												</Link>
												to learn how you could be saving time with automatic file checks.
											</Alert>
										</div>
									)}
							</div>
						),
					},
				]}
				footerActions={[
					{
						key: "SCANNER",
						title: "Scan Documents",
						style: {
							align: "right",
							notCollapsible: true,
							icon: "scanner",
							primary: true,
						},
						hideOnDesktop: true,
						hideOnMobile: !isNative,
						onClick: () => {
							window?.ReactNativeWebView?.postMessage(
								JSON.stringify({ type: "DOCUMENT_SCAN", data: { fileName: fileNameId } })
							);
						},
					},
					{
						key: "UPLOAD",
						title: isNative ? "Upload Images" : "Upload Files",
						style: {
							align: "RIGHT",
							notCollapsible: true,
							icon: "note_add",
							primary: true,
						},
						hideOnDesktop: true,
						hideOnMobile: false,
						onClick: () => {
							fileInputRef?.current?.click();
						},
					},
				]}
			></SmarthopDialogViewContainer>
		</>
	);
});

export default TripFilesForm;
